import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '@/config/api';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Eye,
  Inbox,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';

import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Badge } from '@/Components/ui/badge';
import { Skeleton } from '@/Components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/Components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/Components/ui/table';
import { cn } from '@/lib/utils';

import RecruiterNavbar from './RecruiterNavbar';
import JobDescriptionModal from './JobDescriptionModal';
import { CandidateUploadModal } from './CandidateUpload/CandidateUploadModal';
import { useRecruiterAuth } from '../../../CompanyAdmin/Context/RecruiterAuthContext';

const FILTER_OPTIONS = {
  status: ['Open', 'Closed'],
  dateBuckets: [
    { id: '30', label: 'Last 30 Days' },
    { id: '90', label: 'Last 3 Months' },
    { id: '180', label: 'Last 6 Months' },
  ],
};

const PER_PAGE = 10;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function prettyDepartment(d) {
  const map = {
    eng: 'Engineering',
    design: 'Design',
    data: 'Data',
    qa: 'QA',
    security: 'Security',
  };
  return map[d] || d;
}

function StatusPill({ status }) {
  const s = (status || '').toLowerCase();
  const variant =
    s === 'open' || s === 'active'
      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
      : 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300';

  return (
    <Badge
      variant="outline"
      className={cn('border-0 font-medium uppercase text-[10px]', variant)}
    >
      {status || '—'}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Filter chip — single multi-select item
// ---------------------------------------------------------------------------

function FilterChip({ label, active, count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex w-full items-center justify-between rounded-md border px-3 py-1.5 text-xs transition-all',
        active
          ? 'border-primary bg-primary/10 text-primary font-medium'
          : 'border-border/60 bg-background text-muted-foreground hover:bg-muted/50 hover:text-foreground',
      )}
    >
      <span className="truncate">{label}</span>
      {typeof count === 'number' && (
        <span
          className={cn(
            'ml-2 shrink-0 rounded px-1.5 py-px text-[10px]',
            active ? 'bg-primary/20' : 'bg-muted',
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Filter section — collapsible group of chips
// ---------------------------------------------------------------------------

function FilterSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between py-2 text-left">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform',
            open && 'rotate-180',
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="pb-3 pt-1">
        <div className="flex flex-col gap-1.5">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function RecruiterMyJobs() {
  const navigate = useNavigate();
  const { recruiter } = useRecruiterAuth();

  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showJobDescription, setShowJobDescription] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    status: [],
    locations: [],
    departments: [],
    vacancy_type: [],
    dateBucket: null,
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch jobs ---------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const fetchJobs = async () => {
      if (!recruiter?.email) return;

      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({ recruiterEmail: recruiter.email });
        const res = await fetch(
          `${API_BASE_URL}/api/recruiter/all-jobs?${params}`,
          { credentials: 'include' },
        );
        if (!res.ok) throw new Error('Failed to fetch jobs');
        const data = await res.json();
        if (!cancelled) setAllJobs(data);
      } catch (e) {
        console.error('Error fetching jobs:', e);
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchJobs();
    return () => {
      cancelled = true;
    };
  }, [recruiter?.email]);

  // Derived data -------------------------------------------------------------
  const dynamicFilterOptions = useMemo(() => {
    const locations = new Set();
    const departments = new Set();
    const vacancyTypes = new Set();
    allJobs.forEach((j) => {
      if (j.location) locations.add(j.location);
      if (j.department) departments.add(j.department);
      if (j.vacancy_type) vacancyTypes.add(j.vacancy_type);
    });
    return {
      locations: [...locations].sort(),
      departments: [...departments].sort(),
      vacancy_type: [...vacancyTypes].sort(),
    };
  }, [allJobs]);

  const counts = useMemo(() => {
    const c = { status: {}, locations: {}, departments: {}, vacancy_type: {} };
    allJobs.forEach((j) => {
      c.status[j.status] = (c.status[j.status] || 0) + 1;
      c.locations[j.location] = (c.locations[j.location] || 0) + 1;
      c.departments[j.department] = (c.departments[j.department] || 0) + 1;
      c.vacancy_type[j.vacancy_type] = (c.vacancy_type[j.vacancy_type] || 0) + 1;
    });
    return c;
  }, [allJobs]);

  const filteredJobs = useMemo(() => {
    const now = new Date();
    const days = (d) => Math.round((now - new Date(d)) / 86_400_000);
    const q = searchTerm.trim().toLowerCase();
    return allJobs.filter((j) => {
      if (q) {
        const inTitle = j.title?.toLowerCase().includes(q);
        const inLocation = j.location?.toLowerCase().includes(q);
        const inDept = (j.department || '').toLowerCase().includes(q);
        if (!inTitle && !inLocation && !inDept) return false;
      }
      if (filters.status.length && !filters.status.includes(j.status)) return false;
      if (filters.locations.length && !filters.locations.includes(j.location))
        return false;
      if (filters.departments.length && !filters.departments.includes(j.department))
        return false;
      if (
        filters.vacancy_type.length &&
        !filters.vacancy_type.includes(j.vacancy_type)
      )
        return false;
      if (filters.dateBucket) {
        const diffDays = days(j.posted_on);
        if (parseInt(filters.dateBucket, 10) < diffDays) return false;
      }
      return true;
    });
  }, [filters, searchTerm, allJobs]);

  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / PER_PAGE));
  const paginated = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return filteredJobs.slice(start, start + PER_PAGE);
  }, [page, filteredJobs]);

  // Active filter count for the "Clear all" affordance
  const activeFilterCount =
    filters.status.length +
    filters.locations.length +
    filters.departments.length +
    filters.vacancy_type.length +
    (filters.dateBucket ? 1 : 0) +
    (searchTerm ? 1 : 0);

  // Handlers -----------------------------------------------------------------
  const toggleFilter = (group, value) => {
    setPage(1);
    setFilters((prev) => {
      if (group === 'dateBucket') {
        return {
          ...prev,
          dateBucket: prev.dateBucket === value ? null : value,
        };
      }
      const existing = prev[group] || [];
      const next = existing.includes(value)
        ? existing.filter((v) => v !== value)
        : [...existing, value];
      return { ...prev, [group]: next };
    });
  };

  const clearAll = () => {
    setFilters({
      status: [],
      locations: [],
      departments: [],
      vacancy_type: [],
      dateBucket: null,
    });
    setSearchTerm('');
    setPage(1);
  };

  const gotoPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewJob = (job) =>
    navigate(`/recruiter/job-details?jobId=${job.job_id}`);

  const handleOpenJD = (e, job) => {
    e.stopPropagation();
    setSelectedJobId(job.job_id);
    setShowJobDescription(true);
  };

  const handleAddCandidate = (e, job) => {
    e.stopPropagation();
    setSelectedJob(job);
    setShowUploadModal(true);
  };

  // Error state --------------------------------------------------------------
  if (error) {
    return (
      <>
        <RecruiterNavbar />
        <div className="mx-auto flex w-full max-w-7xl items-center justify-center px-4 py-16">
          <Card className="w-full max-w-md border-destructive/30">
            <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <h2 className="text-lg font-semibold">Error loading jobs</h2>
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // Render -------------------------------------------------------------------
  return (
    <>
      <RecruiterNavbar />

      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">All Jobs</h1>
            <p className="text-sm text-muted-foreground">
              {loading
                ? 'Loading...'
                : `${filteredJobs.length} of ${allJobs.length} jobs`}
            </p>
          </div>

          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search jobs by title, location or dept..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="pl-9"
            />
          </div>
        </div>

        {/* Active filter chips bar (shown when any filter is active) */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Active filters:</span>
            {filters.status.map((s) => (
              <FilterChipPill
                key={`status-${s}`}
                label={s}
                onRemove={() => toggleFilter('status', s)}
              />
            ))}
            {filters.locations.map((l) => (
              <FilterChipPill
                key={`loc-${l}`}
                label={l}
                onRemove={() => toggleFilter('locations', l)}
              />
            ))}
            {filters.departments.map((d) => (
              <FilterChipPill
                key={`dep-${d}`}
                label={prettyDepartment(d)}
                onRemove={() => toggleFilter('departments', d)}
              />
            ))}
            {filters.vacancy_type.map((v) => (
              <FilterChipPill
                key={`vt-${v}`}
                label={v === 'fresher' ? 'Fresher' : 'Experienced'}
                onRemove={() => toggleFilter('vacancy_type', v)}
              />
            ))}
            {filters.dateBucket && (
              <FilterChipPill
                label={
                  FILTER_OPTIONS.dateBuckets.find(
                    (b) => b.id === filters.dateBucket,
                  )?.label
                }
                onRemove={() => toggleFilter('dateBucket', filters.dateBucket)}
              />
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAll}
              className="h-7 px-2 text-xs"
            >
              Clear all
            </Button>
          </div>
        )}

        {/* Main grid */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_280px]">
          {/* Job table */}
          <Card className="border border-border/40 shadow-sm">
            {loading ? (
              <CardContent className="space-y-3 p-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full rounded" />
                ))}
              </CardContent>
            ) : paginated.length === 0 ? (
              <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
                <Inbox className="h-10 w-10 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">
                  No jobs match the selected filters.
                </p>
              </CardContent>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/40">
                        <TableHead>Position</TableHead>
                        <TableHead className="text-right">Vacancies</TableHead>
                        <TableHead className="text-right">
                          Applications
                        </TableHead>
                        <TableHead className="text-right">Shortlisted</TableHead>
                        <TableHead className="text-right">Hired</TableHead>
                        <TableHead className="text-right">Joined</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginated.map((job) => (
                        <TableRow
                          key={job.job_id}
                          onClick={() => handleViewJob(job)}
                          className="cursor-pointer border-border/40 hover:bg-muted/40"
                        >
                          <TableCell className="py-3">
                            <div className="font-medium">{job.title}</div>
                            <div className="text-xs text-muted-foreground">
                              {job.location} · Opened {formatDate(job.posted_on)}
                            </div>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {job.total_vacancies ?? 1}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {job.applications}
                          </TableCell>
                          <ClickableNumberCell
                            value={job.shortlisted}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/recruiter/job-details?jobId=${job.job_id}`,
                                { state: { filter: 'shortlisted' } },
                              );
                            }}
                          />
                          <ClickableNumberCell
                            value={job.hired}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/recruiter/job-details?jobId=${job.job_id}`,
                                { state: { filter: 'hired' } },
                              );
                            }}
                          />
                          <ClickableNumberCell
                            value={job.joined ?? 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/recruiter/job-details?jobId=${job.job_id}`,
                                { state: { filter: 'joined' } },
                              );
                            }}
                          />
                          <TableCell>
                            <StatusPill status={job.status} />
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => handleOpenJD(e, job)}
                              >
                                <Eye className="mr-1 h-3.5 w-3.5" />
                                JD
                              </Button>
                              <Button
                                size="sm"
                                variant="default"
                                onClick={(e) => handleAddCandidate(e, job)}
                              >
                                <Plus className="mr-1 h-3.5 w-3.5" />
                                Add
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between border-t border-border/40 px-4 py-3">
                  <span className="text-xs text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => gotoPage(page - 1)}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <Button
                        key={i}
                        size="sm"
                        variant={page === i + 1 ? 'default' : 'ghost'}
                        onClick={() => gotoPage(i + 1)}
                        className="h-8 w-8 p-0"
                      >
                        {i + 1}
                      </Button>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => gotoPage(page + 1)}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Card>

          {/* Filters sidebar */}
          <aside>
            <Card className="border border-border/40 shadow-sm sticky top-20">
              <div className="flex items-center justify-between border-b border-border/40 px-4 py-3">
                <h3 className="text-sm font-semibold">Filters</h3>
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAll}
                    className="h-7 px-2 text-xs"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <CardContent className="px-4 py-2">
                <FilterSection title="Status" defaultOpen>
                  {FILTER_OPTIONS.status.map((s) => (
                    <FilterChip
                      key={s}
                      label={s}
                      active={filters.status.includes(s)}
                      count={counts.status[s] || 0}
                      onClick={() => toggleFilter('status', s)}
                    />
                  ))}
                </FilterSection>

                <FilterSection title="Locations">
                  {dynamicFilterOptions.locations.map((loc) => (
                    <FilterChip
                      key={loc}
                      label={loc}
                      active={filters.locations.includes(loc)}
                      count={counts.locations[loc] || 0}
                      onClick={() => toggleFilter('locations', loc)}
                    />
                  ))}
                </FilterSection>

                <FilterSection title="Departments">
                  {dynamicFilterOptions.departments.map((d) => (
                    <FilterChip
                      key={d}
                      label={prettyDepartment(d)}
                      active={filters.departments.includes(d)}
                      count={counts.departments[d] || 0}
                      onClick={() => toggleFilter('departments', d)}
                    />
                  ))}
                </FilterSection>

                <FilterSection title="Vacancy Type">
                  {dynamicFilterOptions.vacancy_type.map((v) => (
                    <FilterChip
                      key={v}
                      label={v === 'fresher' ? 'Fresher' : 'Experienced'}
                      active={filters.vacancy_type.includes(v)}
                      count={counts.vacancy_type[v] || 0}
                      onClick={() => toggleFilter('vacancy_type', v)}
                    />
                  ))}
                </FilterSection>

                <FilterSection title="Position Date Opened">
                  {FILTER_OPTIONS.dateBuckets.map((d) => (
                    <FilterChip
                      key={d.id}
                      label={d.label}
                      active={filters.dateBucket === d.id}
                      onClick={() => toggleFilter('dateBucket', d.id)}
                    />
                  ))}
                </FilterSection>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>

      {/* Modals */}
      {selectedJobId && showJobDescription && (
        <JobDescriptionModal
          jobId={selectedJobId}
          recruiterEmail={recruiter?.email}
          onClose={() => {
            setSelectedJobId(null);
            setShowJobDescription(false);
          }}
        />
      )}

      <CandidateUploadModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        jobId={selectedJob?.job_id}
        jobTitle={selectedJob?.title}
        recruiterEmail={recruiter?.email}
        onSuccess={() => window.dispatchEvent(new Event('refreshJobs'))}
      />
    </>
  );
}

function ClickableNumberCell({ value, onClick }) {
  return (
    <TableCell
      className="cursor-pointer text-right font-medium tabular-nums hover:text-primary"
      onClick={onClick}
    >
      {value}
    </TableCell>
  );
}

function FilterChipPill({ label, onRemove }) {
  return (
    <Badge
      variant="outline"
      className="gap-1 border-primary/40 bg-primary/5 pr-1 text-primary"
    >
      <span className="truncate max-w-[140px]">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="rounded p-0.5 hover:bg-primary/10"
        aria-label={`Remove ${label}`}
      >
        <X className="h-3 w-3" />
      </button>
    </Badge>
  );
}

export default RecruiterMyJobs;
